import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MultiCodeEditor from '../components/MultiCodeEditor';
import { months as staticMonths, curriculum as staticCurriculum } from '../data/curriculum';
import { buildStudioEntryUrl } from '../config/studio';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Lock,
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Code2,
  Trophy,
  Flame,
  Gauge,
  LayoutDashboard,
  Search,
  Award,
  ScrollText,
  FolderKanban,
  Settings,
  Menu,
  Zap,
  GraduationCap,
  Briefcase,
  Brain,
  Database,
  Cloud,
  Layers,
  ArrowRight,
  Pencil,
  Plus,
  Trash2,
  X,
  ListChecks,
  FileText,
  GitBranch,
  Lightbulb,
  ExternalLink,
  Sparkles,
  Bell,
  Loader2,
} from 'lucide-react';

const CATEGORIES = [
  {
    id: 'school',
    label: '8th – 12th Standard',
    sublabel: 'School Students · Age 13–18',
    Icon: GraduationCap,
    gradient: 'from-violet-600 via-purple-700 to-indigo-800',
    courses: [
      { id: 'coding-foundations', title: 'Coding & Technology Foundations', subtitle: 'For Classes 8–12', Icon: GraduationCap, gradient: 'from-violet-500 to-purple-600', active: false, badge: 'Coming Soon' },
    ],
  },
  {
    id: 'graduate',
    label: 'Graduate Students',
    sublabel: 'College & University',
    Icon: BookOpen,
    gradient: 'from-emerald-500 via-teal-600 to-green-700',
    courses: [
      { id: 'fullstack-bootcamp', title: 'Full Stack Dev Bootcamp', subtitle: 'For Graduate & Final-Year Students', Icon: Layers, gradient: 'from-emerald-500 to-teal-600', active: false, badge: 'Coming Soon' },
    ],
  },
  {
    id: 'internship',
    label: 'Professional Internship',
    sublabel: 'Industry Training Programs',
    Icon: Briefcase,
    gradient: 'from-cyan-500 via-blue-600 to-violet-700',
    courses: [
      { id: 'aiml',        title: 'AI / ML Engineering',  subtitle: '120-Day Intensive',            Icon: Brain,    gradient: 'from-cyan-500 to-blue-600',    active: true,  badge: 'Active' },
      { id: 'webdev',      title: 'Full Stack Web Dev',    subtitle: 'React · Node.js · MongoDB',    Icon: Code2,    gradient: 'from-violet-500 to-purple-600', active: false, badge: 'Coming Soon' },
      { id: 'datascience', title: 'Data Science',          subtitle: 'Python · SQL · Visualization', Icon: Database, gradient: 'from-emerald-500 to-teal-600', active: false, badge: 'Coming Soon' },
      { id: 'cloud',       title: 'Cloud & DevOps',        subtitle: 'AWS · Docker · Kubernetes',    Icon: Cloud,    gradient: 'from-orange-500 to-red-500',   active: false, badge: 'Coming Soon' },
    ],
  },
];

const AVAILABLE_COURSES = [
  { month: 1, name: 'Python', level: 'Foundation', duration: '14 days', status: 'Available', progress: 0, details: 'Core Python syntax, functions, data structures, files, and practical problem solving.', lessons: ['Python syntax and variables', 'Control flow and functions', 'Lists, dictionaries, and tuples', 'File handling and exceptions', 'Practice problems and mini tasks'] },
  { month: 2, name: 'FastAPI', level: 'Backend', duration: '10 days', status: 'Locked', progress: 0, details: 'API design, routing, validation, auth basics, and production-ready service patterns.', lessons: ['FastAPI project setup', 'Routes, request bodies, and validation', 'Authentication basics', 'Database integration patterns', 'API testing and deployment checks'] },
  { month: 3, name: 'Machine Learning', level: 'Intermediate', duration: '24 days', status: 'Locked', progress: 0, details: 'Supervised learning, model evaluation, feature engineering, and applied ML workflows.', lessons: ['Data preprocessing workflow', 'Regression and classification', 'Model evaluation metrics', 'Feature engineering', 'ML project packaging'] },
  { month: 4, name: 'Advanced Deep Learning', level: 'Advanced', duration: '20 days', status: 'Locked', progress: 0, details: 'Neural networks, CNNs, sequence models, optimization, and model training practice.', lessons: ['Neural network fundamentals', 'CNN architecture practice', 'Sequence models', 'Training optimization', 'Deep learning project review'] },
  { month: 5, name: 'RAG & Production AI', level: 'Professional', duration: '28 days', status: 'Locked', progress: 0, details: 'Retrieval augmented generation, deployment, monitoring, evaluation, and production AI reliability.', lessons: ['Document ingestion', 'Embeddings and vector search', 'RAG application design', 'LLM evaluation', 'Production monitoring'] },
  { month: 6, name: 'Capstone Project', level: 'Portfolio', duration: '18 days', status: 'Locked', progress: 0, details: 'End-to-end project planning, implementation, submission, review, and presentation.', lessons: ['Project scope and planning', 'Implementation milestones', 'Submission checklist', 'Review feedback loop', 'Final presentation'] },
];

const DASHBOARD_NAV_ITEMS = [
  { id: 'search', label: 'Search Engine', Icon: Search },
  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'studio', label: 'Flyers Studio', Icon: Sparkles },
  { id: 'courses', label: 'Courses', Icon: BookOpen },
  { id: 'achievements', label: 'Achievements', Icon: Award },
  { id: 'certificates', label: 'Certificates', Icon: ScrollText },
  { id: 'projects', label: 'Projects', Icon: FolderKanban },
  { id: 'settings', label: 'Settings', Icon: Settings },
];

export default function Dashboard() {
  const { token, API, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [progress, setProgress] = useState([]);
  const [activeMonth, setActiveMonth] = useState('1');
  const [activeSection, setActiveSection] = useState('dashboard');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [courseQuery, setCourseQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState(null);
  // Admin course browser states
  const [browsingCourses, setBrowsingCourses] = useState(false);
  const [adminSelectedCategory, setAdminSelectedCategory] = useState(null);
  const [adminSelectedCourse, setAdminSelectedCourse] = useState(null);
  // PostgreSQL curriculum (source of truth when available)
  const [pgCurriculum, setPgCurriculum] = useState(null); // array of day objects
  const [pgMonths, setPgMonths] = useState(null); // months structure built from PG days
  // Use PG data when available, otherwise static
  const curriculum = pgCurriculum || staticCurriculum;
  const months = pgMonths || staticMonths;
  // Admin AI proposal states
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [pendingProposalCount, setPendingProposalCount] = useState(0);
  // Admin curriculum editing states
  const [curriculumOverrides, setCurriculumOverrides] = useState({});
  const [editingDay, setEditingDay] = useState(null);
  const [editTopic, setEditTopic] = useState('');
  const [editVideos, setEditVideos] = useState([]);
  const [editSaving, setEditSaving] = useState(false);
  const [openingStudio, setOpeningStudio] = useState(false);

  const openCourseBrowser = () => {
    setAdminSelectedCategory(null);
    setAdminSelectedCourse(null);
    setBrowsingCourses(true);
  };

  const closeCourseBrowser = () => {
    setBrowsingCourses(false);
    setAdminSelectedCategory(null);
    setAdminSelectedCourse(null);
  };

  // Build months structure from flat PG days array
  const buildMonthsFromDays = (days) => {
    const monthMap = {};
    days.forEach((d) => {
      const m = d.month || 1;
      const w = d.week || 1;
      if (!monthMap[m]) {
        monthMap[m] = { id: m, title: d.monthTitle || `Month ${m}`, subtitle: '', weeks: {} };
      }
      if (!monthMap[m].weeks[w]) {
        monthMap[m].weeks[w] = { id: w, title: d.weekTitle || `Week ${w}`, days: [] };
      }
      monthMap[m].weeks[w].days.push(d.day);
    });
    return Object.values(monthMap)
      .sort((a, b) => a.id - b.id)
      .map((m) => ({
        ...m,
        weeks: Object.values(m.weeks).sort((a, b) => a.id - b.id),
      }));
  };

  // Fetch published curriculum from PostgreSQL
  const fetchPgCurriculum = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pg-curriculum/published?course_slug=aiml`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.days?.length > 0) {
        setPgCurriculum(res.data.days);
        setPgMonths(buildMonthsFromDays(res.data.days));
      }
    } catch (err) {
      // PG not available — fall back to static data silently
      console.log('PG curriculum not available, using static data');
    }
  }, [API, token]);

  // Fetch pending proposal count (admin only)
  const fetchPendingCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/admin/pg-curriculum/proposals/pending-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingProposalCount(res.data.count || 0);
    } catch (err) {
      // Ignore — badge just won't show
    }
  }, [API, token, isAdmin]);

  useEffect(() => {
    fetchPgCurriculum();
    fetchPendingCount();
  }, [fetchPgCurriculum, fetchPendingCount]);

  // AI proposal submission
  const handleAiSubmit = async () => {
    if (!aiInstruction.trim()) {
      toast.error('Please enter an instruction for the AI');
      return;
    }
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/pg-curriculum/ai-proposals`,
        { course_slug: 'aiml', instruction: aiInstruction.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('AI proposal created!', {
        description: res.data.summary?.slice(0, 100),
      });
      setShowAiModal(false);
      setAiInstruction('');
      fetchPendingCount();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to generate AI proposal';
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const fetchOverrides = useCallback(async () => {
    try {
      const endpoint = isAdmin
        ? `${API}/admin/curriculum/overrides`
        : `${API}/curriculum/overrides`;
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const map = {};
      res.data.forEach((o) => { map[o.day_number] = o; });
      setCurriculumOverrides(map);
    } catch (err) {
      console.error('Failed to fetch curriculum overrides', err);
    }
  }, [API, token, isAdmin]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  useEffect(() => {
    if (isAdmin && adminSelectedCourse) {
      fetchOverrides();
    }
  }, [isAdmin, adminSelectedCourse, fetchOverrides]);

  const handleEditDay = (dayNum) => {
    const curDay = curriculum.find((d) => d.day === dayNum);
    const override = curriculumOverrides[dayNum];
    setEditingDay(dayNum);
    setEditTopic(override?.topic ?? curDay?.topic ?? '');
    setEditVideos(
      override?.resource_links ??
      curDay?.resourceLinks?.map((r) => ({ title: r.title, url: r.url })) ??
      []
    );
  };

  const handleSaveEdit = async () => {
    if (!editingDay) return;
    setEditSaving(true);
    try {
      await axios.put(
        `${API}/admin/curriculum/${editingDay}`,
        { topic: editTopic, resource_links: editVideos },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurriculumOverrides((prev) => ({
        ...prev,
        [editingDay]: { ...(prev[editingDay] || {}), topic: editTopic, resource_links: editVideos },
      }));
      toast.success('Day updated successfully!');
      setEditingDay(null);
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setEditSaving(false);
    }
  };

  const fetchProgress = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProgress(res.data);
    } catch (err) {
      console.error('Failed to fetch progress', err);
    }
  }, [API, token]);

  // Fetch on every mount so navigating back from a day always shows fresh data
  useEffect(() => {
    fetchProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Also re-fetch whenever token/API changes (login state change)
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const getDayProgress = (dayNum) => progress.find((p) => p.day_number === dayNum);

  // Display: shows ✓ on day cards — true when all tasks done OR backend confirmed
  const isDayCompleted = (dayNum) => {
    const p = getDayProgress(dayNum);
    if (p?.is_completed === true) return true;
    const dayData = curriculum.find((d) => d.day === dayNum);
    if (!dayData || !p?.completed_tasks) return false;
    return p.completed_tasks.length >= dayData.tasks.length;
  };

  // Access control: day card only clickable after previous day has is_completed (git required)
  const isDayUnlocked = (dayNum) => {
    if (isAdmin) return true;
    if (dayNum === 1) return true;
    const prev = getDayProgress(dayNum - 1);
    return prev?.is_completed === true;
  };

  const getDayCompletionPercent = (dayNum) => {
    const dayData = curriculum.find((d) => d.day === dayNum);
    if (!dayData) return 0;
    const p = getDayProgress(dayNum);
    if (!p || !p.completed_tasks) return 0;
    return Math.round((p.completed_tasks.length / dayData.tasks.length) * 100);
  };

  const handleDayClick = (dayNum, unlocked) => {
    if (unlocked) {
      navigate(`/dashboard/day/${dayNum}`);
    } else {
      toast.warning('Day Locked', {
        description: `Please complete Day ${dayNum - 1} before accessing Day ${dayNum}.`,
        duration: 3000,
      });
    }
  };

  const totalCompleted = curriculum.filter((d) => isDayCompleted(d.day)).length;
  const overallPercent = Math.round((totalCompleted / 120) * 100);
  const currentStreak = (() => {
    let streak = 0;
    for (let i = 1; i <= 120; i += 1) {
      if (isDayCompleted(i)) streak += 1;
      else break;
    }
    return streak;
  })();

  const currentDay = currentStreak + 1 > 120 ? 120 : currentStreak + 1;

  const openFlyersStudio = async () => {
    setOpeningStudio(true);
    try {
      const response = await axios.post(
        `${API}/auth/studio-token`,
        { day_number: currentDay },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.assign(buildStudioEntryUrl(response.data.token));
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(message || 'Unable to open Flyers Studio. Please try again.');
      setOpeningStudio(false);
    }
  };

  const normalizedCourseQuery = courseQuery.trim().toLowerCase();
  const matchedCourse = normalizedCourseQuery
    ? AVAILABLE_COURSES.find((course) => course.name.toLowerCase() === normalizedCourseQuery)
    : null;

  const handleSectionChange = (sectionId) => {
    if (sectionId === 'studio') {
      setMobileNavOpen(false);
      openFlyersStudio();
      return;
    }
    setActiveSection(sectionId);
    if (sectionId !== 'courses') setSelectedModule(null);
    setMobileNavOpen(false);
  };

  const handleModuleClick = (course) => {
    if (course.status === 'Locked') {
      toast.warning('This course is locked');
      return;
    }

    setSelectedModule(course);
  };

  const renderDashboardContent = () => (
    <>
        <section className="relative mb-8 overflow-hidden rounded-2xl border border-violet-300 bg-gradient-to-r from-violet-100 via-fuchsia-50 to-cyan-50 p-6 shadow-md dark:border-violet-500/30 dark:from-violet-950/60 dark:via-fuchsia-950/30 dark:to-cyan-950/40">
          <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/30">
              <Sparkles className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white">Flyers Studio</h2>
                <Badge className="border-violet-400/40 bg-violet-500/15 text-violet-700 dark:text-violet-300">AI Classroom</Badge>
              </div>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                Create AI-powered classroom content using your FlyersMinds account. No second login required.
              </p>
            </div>
            <button
              type="button"
              onClick={openFlyersStudio}
              disabled={openingStudio}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-violet-500/25 transition hover:from-violet-500 hover:to-fuchsia-500 disabled:cursor-wait disabled:opacity-60"
            >
              {openingStudio ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {openingStudio ? 'Opening Studio…' : 'Open Flyers Studio'}
            </button>
          </div>
        </section>

        {/* Check Where You Are — Quiz card (intern only) */}
        {!isAdmin && (
          <div className="mb-8 overflow-hidden rounded-2xl border border-purple-200 dark:border-purple-500/20 bg-gradient-to-r from-purple-50 dark:from-purple-950/20 to-indigo-50 dark:to-indigo-950/20 p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/30">
                <ListChecks className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">Check Where You Are</h3>
                <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">Take this 20-question self-assessment quiz covering Python, FastAPI, ML, Deep Learning &amp; RAG to test your knowledge.</p>
              </div>
              <button
                onClick={() => navigate('/quiz')}
                className="shrink-0 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:from-purple-500 hover:to-indigo-500 shadow-md shadow-purple-500/20 transition-all whitespace-nowrap"
              >
                Start Quiz →
              </button>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <section className="relative mb-10 overflow-hidden rounded-2xl border border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 p-8 backdrop-blur-md shadow-md dark:shadow-none">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyan-500/10 dark:bg-cyan-500/20 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 h-64 w-64 rounded-full bg-purple-500/10 dark:bg-purple-500/20 blur-3xl" />

          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-heading text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl">
                Welcome back{user?.name ? (
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600">, {user.name.split(' ')[0]}</span>
                ) : ''}
              </h1>
              <p className="mt-3 max-w-2xl text-base text-slate-600 dark:text-slate-400">
                {isAdmin
                  ? 'You are viewing the full course as an admin. All 120 days are unlocked for preview.'
                  : 'Stay on rhythm. Complete today\'s tasks and unlock your next milestone in the AI/ML journey.'}
              </p>
              {isAdmin && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
                  <span className="h-2 w-2 rounded-full bg-amber-500" />
                  Admin Preview — All days unlocked
                </div>
              )}
            </div>

            {isAdmin ? (
              <Button
                onClick={openCourseBrowser}
                className="h-11 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-6 text-sm font-semibold text-white hover:from-cyan-500 hover:to-blue-500 shadow-md shadow-cyan-500/20"
              >
                Course Access
              </Button>
            ) : (
              <div className="flex items-center gap-4 rounded-xl border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-slate-900/40 p-4 backdrop-blur-sm shadow-sm dark:shadow-none">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30">
                  <Gauge className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Current Focus</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold text-slate-900 dark:text-white">Day {currentDay}</span>
                    <span className="text-sm text-slate-500">of 120</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleDayClick(currentDay, true)}
                  className="ml-2 h-10 rounded-lg bg-slate-900 dark:bg-white/10 px-4 text-sm font-semibold text-white hover:bg-slate-800 dark:hover:bg-white/20"
                >
                  Resumé
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Stats Grid — interns only */}
        {!isAdmin && <div className="mb-10 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="glass-card group relative overflow-hidden rounded-2xl p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 dark:from-cyan-500/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative z-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400">
                  <BookOpen className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-slate-500">TOTAL</span>
              </div>
              <p className="font-heading text-3xl font-bold text-slate-900 dark:text-white">{overallPercent}%</p>
              <Progress value={overallPercent} className="mt-4 h-1.5 bg-slate-200 dark:bg-slate-800" indicatorClassName="bg-cyan-500" />
            </div>
          </div>

          <div className="glass-card group relative overflow-hidden rounded-2xl p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 dark:from-green-500/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative z-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-slate-500">DONE</span>
              </div>
              <p className="font-heading text-3xl font-bold text-slate-900 dark:text-white">
                {totalCompleted}
                <span className="ml-1 text-sm font-medium text-slate-500">/ 120</span>
              </p>
              <div className="mt-4 text-xs text-green-600 dark:text-green-400 font-medium">Tasks Completed</div>
            </div>
          </div>

          <div className="glass-card group relative overflow-hidden rounded-2xl p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 dark:from-orange-500/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative z-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400">
                  <Flame className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-slate-500">STREAK</span>
              </div>
              <p className="font-heading text-3xl font-bold text-orange-500 drop-shadow-sm dark:drop-shadow-[0_0_10px_rgba(249,115,22,0.5)]">
                {currentStreak}
              </p>
              <div className="mt-4 text-xs text-orange-600 dark:text-orange-400 font-medium">Day Streak!</div>
            </div>
          </div>

          <div className="glass-card group relative overflow-hidden rounded-2xl p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 dark:from-purple-500/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
            <div className="relative z-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400">
                  <Zap className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-slate-500">XP</span>
              </div>
              <p className="font-heading text-3xl font-bold text-slate-900 dark:text-white">
                {totalCompleted * 100}
              </p>
              <div className="mt-4 text-xs text-purple-600 dark:text-purple-400 font-medium">Points Earned</div>
            </div>
          </div>
        </div>}

        {/* Admin: Course Browser (shown only when Course Access button clicked) */}
        {isAdmin && browsingCourses && (
          <>
            {!adminSelectedCategory ? (
              /* Level 1: Category tiles */
              <div>
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">Course Access</h2>
                    <p className="mt-1 text-slate-500 dark:text-slate-400">Select a category to explore course content.</p>
                  </div>
                  <button
                    onClick={closeCourseBrowser}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back to Dashboard
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  {CATEGORIES.map((cat) => {
                    const { Icon } = cat;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setAdminSelectedCategory(cat.id)}
                        className="group relative overflow-hidden rounded-2xl p-px text-left focus:outline-none"
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${cat.gradient} opacity-90 group-hover:opacity-100 transition-opacity rounded-2xl`} />
                        <div className="relative z-10 rounded-[15px] bg-slate-950/60 backdrop-blur-sm p-7 h-full flex flex-col gap-4 border border-white/10">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 shadow-inner">
                            <Icon className="h-7 w-7 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-white">{cat.label}</h3>
                            <p className="mt-1 text-sm text-white/70">{cat.sublabel}</p>
                          </div>
                          <div className="flex items-center gap-2 text-sm font-semibold text-white/80 group-hover:text-white transition-colors">
                            Explore courses <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : !adminSelectedCourse ? (
              /* Level 2: Course tiles within selected category */
              (() => {
                const cat = CATEGORIES.find((c) => c.id === adminSelectedCategory);
                return (
                  <div>
                    <div className="mb-6 flex items-center gap-3">
                      <button
                        onClick={() => setAdminSelectedCategory(null)}
                        className="flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                      >
                        <ChevronLeft className="h-4 w-4" /> All Categories
                      </button>
                      <span className="text-slate-400">/</span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-white">{cat.label}</span>
                    </div>
                    <div className="mb-6">
                      <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{cat.label}</h2>
                      <p className="mt-1 text-slate-500 dark:text-slate-400">{cat.sublabel}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {cat.courses.map((course) => {
                        const { Icon: CourseIcon } = course;
                        return (
                          <button
                            key={course.id}
                            onClick={() => course.active && setAdminSelectedCourse(course.id)}
                            disabled={!course.active}
                            className={`group relative overflow-hidden rounded-2xl p-px text-left focus:outline-none ${!course.active ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <div className={`absolute inset-0 bg-gradient-to-br ${course.gradient} ${course.active ? 'opacity-80 group-hover:opacity-100' : 'opacity-60'} transition-opacity rounded-2xl`} />
                            <div className="relative z-10 rounded-[15px] bg-slate-950/60 backdrop-blur-sm p-6 h-full flex flex-col gap-3 border border-white/10">
                              <div className="flex items-start justify-between">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 border border-white/20">
                                  <CourseIcon className="h-6 w-6 text-white" />
                                </div>
                                <span className={`rounded-full px-3 py-1 text-xs font-bold ${course.active ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/40' : 'bg-white/10 text-white/60 border border-white/10'}`}>
                                  {course.badge}
                                </span>
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-bold text-white">{course.title}</h3>
                                <p className="mt-0.5 text-sm text-white/70">{course.subtitle}</p>
                              </div>
                              {course.active && (
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-white/80 group-hover:text-white transition-colors">
                                  View curriculum <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            ) : (
              /* Level 3: Curriculum */
              <>
                <div className="mb-6 flex items-center gap-3">
                  <button
                    onClick={() => setAdminSelectedCourse(null)}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" /> Back to Courses
                  </button>
                  <span className="text-slate-400">/</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">{CATEGORIES.find(c => c.id === adminSelectedCategory)?.label}</span>
                  <span className="text-slate-400">/</span>
                  <span className="text-sm font-semibold text-slate-700 dark:text-white">
                    {CATEGORIES.flatMap(c => c.courses).find(c => c.id === adminSelectedCourse)?.title}
                  </span>
                </div>

                {/* Admin AI Curriculum Actions */}
                <div className="mb-6 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setShowAiModal(true)}
                    className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white hover:from-violet-500 hover:to-purple-500 shadow-md shadow-violet-500/20 transition-all"
                  >
                    <Sparkles className="h-4 w-4" />
                    Generate AI Curriculum Update
                  </button>
                  <button
                    onClick={() => navigate('/admin/curriculum-proposals')}
                    className="relative flex items-center gap-2 rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <Bell className="h-4 w-4" />
                    Review Proposals
                    {pendingProposalCount > 0 && (
                      <span className="absolute -top-2 -right-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                        {pendingProposalCount}
                      </span>
                    )}
                  </button>
                </div>

                <CurriculumTabs
                  months={months}
                  curriculum={curriculum}
                  activeMonth={activeMonth}
                  setActiveMonth={setActiveMonth}
                  isDayUnlocked={isDayUnlocked}
                  isDayCompleted={isDayCompleted}
                  getDayCompletionPercent={getDayCompletionPercent}
                  handleDayClick={handleDayClick}
                  isAdmin={isAdmin}
                  overrides={curriculumOverrides}
                  onEditDay={handleEditDay}
                />
              </>
            )}
          </>
        )}

        {/* Intern curriculum (always visible) */}
        {!isAdmin && (
          <CurriculumTabs
            months={months}
            curriculum={curriculum}
            activeMonth={activeMonth}
            setActiveMonth={setActiveMonth}
            isDayUnlocked={isDayUnlocked}
            isDayCompleted={isDayCompleted}
            getDayCompletionPercent={getDayCompletionPercent}
            handleDayClick={handleDayClick}
            overrides={curriculumOverrides}
          />
        )}
      </>
  );

  const renderActiveSection = () => {
    if (activeSection === 'dashboard') return renderDashboardContent();

    if (activeSection === 'search') {
      return (
        <DashboardPanel
          title="Search Engine"
          subtitle="Search the available FlyersMinds courses by course name."
          Icon={Search}
        >
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200">
                Course name
              </label>
              <input
                value={courseQuery}
                onChange={(e) => setCourseQuery(e.target.value)}
                placeholder="Try Python, FastAPI, RAG & Production AI..."
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 dark:border-white/10 dark:bg-slate-950/60 dark:text-white"
              />
            </div>

            {normalizedCourseQuery && (
              matchedCourse ? (
                <div className="rounded-2xl border border-cyan-200 bg-cyan-50/70 p-5 dark:border-cyan-500/20 dark:bg-cyan-500/10">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{matchedCourse.name}</h3>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{matchedCourse.details}</p>
                    </div>
                    <Badge className="border-cyan-400/40 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300">
                      {matchedCourse.status}
                    </Badge>
                  </div>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <InfoTile label="Level" value={matchedCourse.level} />
                    <InfoTile label="Duration" value={matchedCourse.duration} />
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm font-semibold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                  Course not available
                </div>
              )
            )}

            <div>
              <p className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Available Courses</p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {AVAILABLE_COURSES.map((course) => (
                  <button
                    key={course.name}
                    onClick={() => setCourseQuery(course.name)}
                    className="rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-cyan-400 hover:bg-cyan-50 dark:border-white/10 dark:bg-white/5 dark:hover:border-cyan-500/40 dark:hover:bg-cyan-500/10"
                  >
                    <p className="font-semibold text-slate-900 dark:text-white">{course.name}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Month {course.month} - {course.status}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DashboardPanel>
      );
    }

    if (activeSection === 'courses') {
      if (selectedModule) {
        return (
          <ModuleDetail
            module={selectedModule}
            onBack={() => setSelectedModule(null)}
          />
        );
      }

      return (
        <DashboardPanel
          title="Courses"
          subtitle="All available FlyersMinds course modules."
          Icon={BookOpen}
        >
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {AVAILABLE_COURSES.map((course) => (
              <CourseCard
                key={course.name}
                course={course}
                isSelected={selectedModule?.name === course.name}
                onClick={() => handleModuleClick(course)}
              />
            ))}
          </div>
        </DashboardPanel>
      );
    }

    if (activeSection === 'achievements') {
      return (
        <DashboardPanel title="Achievements" subtitle="Your unlocked milestones will appear here." Icon={Award}>
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 p-8 text-center dark:border-white/10 dark:bg-white/5">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300">
              <Award className="h-7 w-7" />
            </div>
            <h3 className="font-heading text-xl font-bold text-slate-900 dark:text-white">No achievements unlocked yet</h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Achievements will appear here as you progress.</p>
          </div>
        </DashboardPanel>
      );
    }

    if (activeSection === 'certificates') {
      return (
        <DashboardPanel title="Certificates" subtitle="Certificate tracks unlock as you complete modules." Icon={ScrollText}>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ['Python Foundations Certificate', 'Complete Python to unlock this certificate.'],
              ['FastAPI Backend Certificate', 'Complete FastAPI to unlock this certificate.'],
              ['Machine Learning Certificate', 'Complete Machine Learning to unlock this certificate.'],
              ['Production AI Certificate', 'Complete Production AI to unlock this certificate.'],
            ].map(([title, copy]) => (
              <LockedCertificateCard
                key={title}
                title={title}
                copy={copy}
                onClick={() => toast.warning('This certificate is locked')}
              />
            ))}
          </div>
        </DashboardPanel>
      );
    }

    if (activeSection === 'projects') {
      return (
        <DashboardPanel title="Projects" subtitle="Track submitted, pending, and upcoming projects." Icon={FolderKanban}>
          <div className="grid gap-4 lg:grid-cols-2">
            <ProjectColumn title="Pending" items={['Python']} tone="amber" />
            <ProjectColumn title="Upcoming" items={['FastAPI', 'Machine Learning', 'Advanced Deep Learning', 'RAG & Production AI', 'Capstone Project']} tone="cyan" />
          </div>
        </DashboardPanel>
      );
    }

    return (
      <DashboardPanel title="Settings" subtitle="Profile details and basic dashboard options." Icon={Settings}>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/5">
            <p className="mb-4 text-sm font-bold text-slate-900 dark:text-white">Profile Details</p>
            <div className="space-y-3">
              <InfoTile label="Name" value={user?.name || 'FlyersMinds Learner'} />
              <InfoTile label="Email" value={user?.email || 'Not available'} />
              <InfoTile label="Role" value={user?.role || 'intern'} />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/5">
            <p className="mb-4 text-sm font-bold text-slate-900 dark:text-white">Basic Options</p>
            <div className="space-y-3">
              {['Email progress updates', 'Weekly milestone reminders', 'Show locked course days'].map((option) => (
                <label key={option} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-slate-950/40 dark:text-slate-300">
                  <span>{option}</span>
                  <input type="checkbox" defaultChecked={option !== 'Show locked course days'} className="h-4 w-4 accent-cyan-500" />
                </label>
              ))}
            </div>
          </div>
        </div>
      </DashboardPanel>
    );
  };

  return (
    <div className="mesh-bg min-h-screen pb-12">
      <Navbar />
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4 px-4 py-8 sm:px-6 lg:flex-row lg:px-8">
        <button
          onClick={() => setMobileNavOpen((open) => !open)}
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm dark:border-white/10 dark:bg-slate-900/70 dark:text-slate-200 lg:hidden"
        >
          <Menu className="h-4 w-4" />
          Navigation
        </button>
        <DashboardSidebar
          activeSection={activeSection}
          isOpen={mobileNavOpen}
          onSectionChange={handleSectionChange}
        />
        <main className="min-w-0 flex-1">
          {renderActiveSection()}
        </main>
      </div>

      {/* Admin Edit Day Modal */}
      {editingDay && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => !editSaving && setEditingDay(null)}
        >
          <div
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-white/10 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">Edit Day {editingDay}</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">Customize topic and learning resources</p>
              </div>
              <button
                onClick={() => setEditingDay(null)}
                disabled={editSaving}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">
              {/* Topic */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-white mb-2">Topic</label>
                <input
                  value={editTopic}
                  onChange={(e) => setEditTopic(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-white/10 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-shadow"
                  placeholder="Day topic..."
                />
              </div>

              {/* YouTube Resources */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-slate-700 dark:text-white">YouTube Resources</label>
                  <button
                    onClick={() => setEditVideos((v) => [...v, { title: '', url: '' }])}
                    className="flex items-center gap-1 rounded-lg bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/30 px-3 py-1.5 text-xs font-semibold text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-500/20 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Video
                  </button>
                </div>

                <div className="space-y-3">
                  {editVideos.map((v, i) => (
                    <div key={i} className="flex gap-2 items-start rounded-xl border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-800/50 p-3">
                      <div className="flex-1 space-y-2">
                        <input
                          value={v.title}
                          onChange={(e) => {
                            const updated = [...editVideos];
                            updated[i] = { ...updated[i], title: e.target.value };
                            setEditVideos(updated);
                          }}
                          placeholder="Video title..."
                          className="w-full rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                        />
                        <input
                          value={v.url}
                          onChange={(e) => {
                            const updated = [...editVideos];
                            updated[i] = { ...updated[i], url: e.target.value };
                            setEditVideos(updated);
                          }}
                          placeholder="YouTube URL (e.g. https://youtu.be/...)"
                          className="w-full rounded-lg border border-slate-300 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
                        />
                      </div>
                      <button
                        onClick={() => setEditVideos((vids) => vids.filter((_, idx) => idx !== i))}
                        className="mt-1 rounded-lg p-2 text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 transition-colors"
                        title="Remove video"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {editVideos.length === 0 && (
                    <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4 rounded-xl border border-dashed border-slate-300 dark:border-white/10">
                      No videos added yet. Click "Add Video" to add one.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-200 dark:border-white/10 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
              <button
                onClick={() => setEditingDay(null)}
                disabled={editSaving}
                className="rounded-xl border border-slate-300 dark:border-white/10 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 py-2 text-sm font-semibold text-white hover:from-cyan-500 hover:to-blue-500 disabled:opacity-60 transition-all shadow-md shadow-cyan-500/20"
              >
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Curriculum Update Modal */}
      {showAiModal && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => !aiLoading && setShowAiModal(false)}
        >
          <div
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-white/10 px-6 py-4 bg-gradient-to-r from-violet-50 dark:from-violet-950/30 to-purple-50 dark:to-purple-950/30">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">AI Curriculum Update</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Describe the changes you want AI to make</p>
                </div>
              </div>
              <button
                onClick={() => setShowAiModal(false)}
                disabled={aiLoading}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-white mb-2">Instruction</label>
                <textarea
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  rows={5}
                  disabled={aiLoading}
                  className="w-full rounded-xl border border-slate-300 dark:border-white/10 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-shadow resize-none"
                  placeholder="e.g. Update days 10-15 to include more hands-on PyTorch exercises..."
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                The AI will read your current published curriculum and generate a full updated proposal. You can review and approve/reject it before it goes live.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 dark:border-white/10 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
              <button
                onClick={() => setShowAiModal(false)}
                disabled={aiLoading}
                className="rounded-xl border border-slate-300 dark:border-white/10 px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAiSubmit}
                disabled={aiLoading || !aiInstruction.trim()}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-2 text-sm font-semibold text-white hover:from-violet-500 hover:to-purple-500 disabled:opacity-60 transition-all shadow-md shadow-violet-500/20"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Proposal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardSidebar({ activeSection, isOpen, onSectionChange }) {
  return (
    <aside className={`${isOpen ? 'block' : 'hidden'} shrink-0 lg:block lg:w-72`}>
      <div className="sticky top-28 rounded-2xl border border-slate-300 bg-white/80 p-3 shadow-md backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/70">
        <div className="mb-3 px-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-600 dark:text-cyan-400">Workspace</p>
          <h2 className="font-heading text-lg font-bold text-slate-900 dark:text-white">FlyersMinds</h2>
        </div>
        <nav className="space-y-1">
          {DASHBOARD_NAV_ITEMS.map(({ id, label, Icon }) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                onClick={() => onSectionChange(id)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition-all ${
                  isActive
                    ? 'border border-cyan-400/40 bg-cyan-100 text-cyan-800 shadow-sm shadow-cyan-500/10 dark:bg-cyan-500/15 dark:text-cyan-200'
                    : 'border border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}

function DashboardPanel({ title, subtitle, Icon, children }) {
  return (
    <section className="rounded-2xl border border-slate-300 bg-white/80 p-5 shadow-md backdrop-blur-md dark:border-white/10 dark:bg-white/5 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{title}</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>
          </div>
        </div>
      </div>
      {children}
    </section>
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-950/40">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}

function CourseCard({ course, isSelected, onClick }) {
  const statusClass = {
    Available: 'border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
    'In Progress': 'border-cyan-300 bg-cyan-100 text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300',
    Locked: 'border-slate-300 bg-slate-100 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400',
  }[course.status];
  const isLocked = course.status === 'Locked';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full rounded-2xl border bg-white p-5 text-left transition hover:border-cyan-400/60 hover:shadow-lg hover:shadow-cyan-500/10 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 dark:bg-white/5 dark:hover:border-cyan-500/40 ${
        isSelected
          ? 'border-cyan-400 shadow-lg shadow-cyan-500/10 dark:border-cyan-500/50'
          : 'border-slate-200 dark:border-white/10'
      } ${isLocked ? 'cursor-pointer opacity-75' : 'cursor-pointer'}`}
    >
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 font-heading text-lg font-bold text-white shadow-lg shadow-cyan-500/20">
          {course.month}
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusClass}`}>
          {course.status}
        </span>
      </div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Month {course.month}</p>
      <h3 className="mt-2 font-heading text-xl font-bold text-slate-900 dark:text-white">{course.name}</h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{course.details}</p>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <InfoTile label="Level" value={course.level} />
        <InfoTile label="Duration" value={course.duration} />
      </div>
      <div className="mt-5 text-sm font-semibold text-cyan-700 dark:text-cyan-300">
        {isLocked ? 'Locked' : course.status === 'In Progress' ? 'Continue' : 'Open module'}
      </div>
    </button>
  );
}

function ModuleDetail({ module, onBack }) {
  const actionLabel = module.status === 'In Progress' ? 'Continue' : 'Start Module';

  return (
    <DashboardPanel
      title={module.name}
      subtitle={`Month ${module.month} module details and learning path.`}
      Icon={BookOpen}
    >
      <button
        type="button"
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Courses
      </button>

      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/5">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">Month {module.month}</p>
              <h2 className="mt-2 font-heading text-3xl font-bold text-slate-900 dark:text-white">{module.name}</h2>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-300">{module.details}</p>
            </div>
            <ModuleStatusBadge status={module.status} />
          </div>

          <div className="mb-6 grid gap-3 sm:grid-cols-3">
            <InfoTile label="Level" value={module.level} />
            <InfoTile label="Duration" value={module.duration} />
            <InfoTile label="Progress" value={`${module.progress}%`} />
          </div>

          <div>
            <h3 className="mb-3 font-heading text-xl font-bold text-slate-900 dark:text-white">Lessons and Topics</h3>
            <div className="space-y-3">
              {module.lessons.map((lesson, index) => (
                <div
                  key={lesson}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-slate-950/40 dark:text-slate-300"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-xs font-bold text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300">
                    {index + 1}
                  </span>
                  {lesson}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-white/5">
          <p className="text-sm font-bold text-slate-900 dark:text-white">Module Progress</p>
          <div className="mt-4">
            <Progress value={module.progress} className="h-2 bg-slate-200 dark:bg-slate-800" indicatorClassName="bg-cyan-500" />
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>{module.progress}% complete</span>
              <span>{module.status}</span>
            </div>
          </div>
          <button
            type="button"
            className="mt-6 w-full rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-cyan-500/20 transition hover:from-cyan-500 hover:to-blue-500"
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </DashboardPanel>
  );
}

function ModuleStatusBadge({ status }) {
  const statusClass = {
    Available: 'border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
    'In Progress': 'border-cyan-300 bg-cyan-100 text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300',
    Locked: 'border-slate-300 bg-slate-100 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400',
  }[status];

  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusClass}`}>
      {status}
    </span>
  );
}

function LockedCertificateCard({ title, copy, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:border-cyan-400/60 hover:shadow-lg hover:shadow-cyan-500/10 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 dark:border-white/10 dark:bg-white/5 dark:hover:border-cyan-500/40"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-cyan-600 dark:bg-slate-950/50 dark:text-cyan-400">
          <ScrollText className="h-5 w-5" />
        </div>
        <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
          Locked
        </span>
      </div>
      <h3 className="font-heading text-lg font-bold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{copy}</p>
    </button>
  );
}

function ProjectColumn({ title, items, tone }) {
  const toneClass = {
    green: 'text-emerald-600 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/20',
    cyan: 'text-cyan-700 dark:text-cyan-300 bg-cyan-500/10 border-cyan-500/20',
  }[tone];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-white/5">
      <h3 className={`mb-4 rounded-xl border px-4 py-2 text-sm font-bold ${toneClass}`}>{title}</h3>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 dark:border-white/10 dark:bg-slate-950/40 dark:text-slate-300">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function Button({ className, ...props }) {
  return <button className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 ${className}`} {...props} />
}

function CurriculumTabs({ months, curriculum, activeMonth, setActiveMonth, isDayUnlocked, isDayCompleted, getDayCompletionPercent, handleDayClick, isAdmin = false, overrides = {}, onEditDay }) {
  const [expandedDay, setExpandedDay] = useState(null);

  const handleRowClick = (dayNum, unlocked) => {
    if (isAdmin && onEditDay) {
      // Admin Course Access: toggle inline notes panel
      setExpandedDay((prev) => (prev === dayNum ? null : dayNum));
    } else {
      handleDayClick(dayNum, unlocked);
    }
  };

  return (
    <Tabs value={activeMonth} onValueChange={setActiveMonth} className="w-full">
      <div className="mb-8 overflow-x-auto pb-2">
        <TabsList className="h-auto w-max gap-2 bg-transparent p-0">
          {months.map((month) => (
            <TabsTrigger
              key={month.id}
              value={String(month.id)}
              className="relative overflow-hidden rounded-xl border border-slate-300 dark:border-white/10 bg-white dark:bg-white/5 px-5 py-3 text-sm font-semibold text-slate-700 dark:text-slate-400 transition-all hover:bg-slate-100 dark:hover:bg-white/10 hover:border-slate-400 dark:hover:border-white/20 data-[state=active]:border-cyan-500/50 data-[state=active]:bg-cyan-100 dark:data-[state=active]:bg-cyan-950/30 data-[state=active]:text-cyan-700 dark:data-[state=active]:text-cyan-400"
            >
              <span className="relative z-10">Month {month.id}: {month.title}</span>
              {activeMonth === String(month.id) && (
                <span className="absolute inset-0 z-0 bg-gradient-to-r from-cyan-500/10 to-transparent" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {months.map((month) => (
        <TabsContent key={month.id} value={String(month.id)} className="mt-0 animate-in fade-in slide-in-from-bottom-5 duration-500">
          <div className="mb-6">
            <h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">
              Month {month.id}: {month.title}
            </h2>
            <p className="text-slate-600 dark:text-slate-400">{month.subtitle}</p>
          </div>

          <div className="space-y-6">
            {month.weeks.map((week) => {
              const weekDaysCompleted = week.days.filter(isDayCompleted).length;
              const weekPercent = Math.round((weekDaysCompleted / week.days.length) * 100);

              return (
                <div key={week.id} className="glass-panel overflow-hidden rounded-2xl border border-slate-300 dark:border-white/5 bg-white dark:bg-slate-900/40">
                  <div className="flex items-center justify-between border-b border-slate-300 dark:border-white/5 bg-slate-100 dark:bg-white/5 px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-300 dark:bg-slate-800 text-cyan-700 dark:text-cyan-400 border border-slate-400 dark:border-white/5">
                        <Code2 className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">
                          Week {week.id}: {week.title}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {weekDaysCompleted}/{week.days.length} days completed
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="hidden sm:block text-right">
                        <div className="custom-progress w-32 h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500" style={{ width: `${weekPercent}%` }} />
                        </div>
                      </div>
                      <span className="min-w-[40px] text-right text-sm font-bold text-slate-900 dark:text-white">
                        {weekPercent}%
                      </span>
                    </div>
                  </div>

                  <div className="divide-y divide-slate-200 dark:divide-white/5">
                    {week.days.map((dayNum) => {
                      const dayData = curriculum.find((d) => d.day === dayNum);
                      if (!dayData) return null;
                      const unlocked = isDayUnlocked(dayNum);
                      const completed = isDayCompleted(dayNum);
                      const completionPct = getDayCompletionPercent(dayNum);
                      const override = overrides[dayNum];
                      const displayTopic = override?.topic ?? dayData.topic;
                      const lowerTopic = displayTopic.toLowerCase();
                      const isMiniProject =
                        lowerTopic.includes('mini project') ||
                        lowerTopic.includes('capstone') ||
                        lowerTopic.includes('final project');

                      const isExpanded = expandedDay === dayNum;

                      return (
                        <div
                          key={dayNum}
                          className={`group w-full transition-all ${unlocked
                            ? 'hover:bg-slate-100 dark:hover:bg-white/5'
                            : 'hover:bg-slate-50 dark:hover:bg-transparent'
                            }`}
                        >
                          <div className="flex w-full items-center">
                          <button
                            onClick={() => handleRowClick(dayNum, unlocked)}
                            className={`flex flex-1 items-center gap-5 px-6 py-4 text-left ${unlocked ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                          >
                            <div className="shrink-0 relative">
                              {completed ? (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 shadow-[0_0_15px_rgba(74,222,128,0.2)]">
                                  <CheckCircle2 className="h-6 w-6" />
                                </div>
                              ) : unlocked ? (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 group-hover:shadow-[0_0_15px_rgba(34,211,238,0.4)] transition-shadow">
                                  <Circle className="h-6 w-6" />
                                </div>
                              ) : (
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-300 dark:bg-slate-800 text-slate-600 dark:text-slate-500">
                                  <Lock className="h-5 w-5" />
                                </div>
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="mb-1.5 flex flex-wrap items-center gap-2">
                                <span className={`text-xs font-bold uppercase tracking-wider ${unlocked ? 'text-cyan-600 dark:text-cyan-400' : 'text-slate-600 dark:text-slate-500'}`}>
                                  Day {dayNum}
                                </span>

                                {!unlocked && (
                                  <Badge variant="secondary" className="bg-slate-300 dark:bg-slate-800 text-slate-700 dark:text-slate-400 border-slate-400 dark:border-slate-700">
                                    <Lock className="mr-1 h-3 w-3" />
                                    Locked
                                  </Badge>
                                )}

                                {isMiniProject && (
                                  <Badge className="bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-500 border-amber-500/30 hover:bg-amber-500/30">
                                    <Trophy className="mr-1 h-3 w-3" />
                                    Project
                                  </Badge>
                                )}

                                {isAdmin && override?.topic && (
                                  <Badge className="bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-500/30">
                                    <Pencil className="mr-1 h-3 w-3" />
                                    Edited
                                  </Badge>
                                )}
                              </div>

                              <p className={`truncate text-sm font-medium ${unlocked ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                                {displayTopic}
                              </p>

                              {unlocked && !completed && completionPct > 0 && (
                                <div className="mt-2 flex items-center gap-3">
                                  <Progress value={completionPct} className="h-1.5 w-24 bg-slate-200 dark:bg-slate-800" indicatorClassName="bg-cyan-500" />
                                  <span className="text-[10px] text-slate-500 dark:text-slate-400">{completionPct}%</span>
                                </div>
                              )}
                            </div>

                            {unlocked && (
                              <ChevronRight className="h-5 w-5 shrink-0 text-slate-500 dark:text-slate-600 transition-transform group-hover:translate-x-1 group-hover:text-cyan-600 dark:group-hover:text-cyan-400" />
                            )}
                          </button>

                          {isAdmin && onEditDay && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onEditDay(dayNum); }}
                              className="mr-4 flex-shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-cyan-50 dark:hover:bg-cyan-500/10 hover:text-cyan-600 dark:hover:text-cyan-400 transition-all opacity-0 group-hover:opacity-100"
                              title={`Edit Day ${dayNum}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          </div>

                          {/* Inline full-detail panel — admin Course Access only */}
                          {isAdmin && onEditDay && isExpanded && (
                            <div className="border-t border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-slate-950/40 px-6 py-6 space-y-6">

                              {/* Lesson Overview */}
                              <div className="space-y-4">
                                <div className="flex items-center gap-2">
                                  <BookOpen className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">Lesson Overview</h4>
                                </div>
                                {dayData.overview && (
                                  <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{dayData.overview}</p>
                                )}
                                {dayData.content && dayData.content.length > 0 ? (
                                  <div className="space-y-4">
                                    {dayData.content.map((section, si) => (
                                      <div key={si}>
                                        <h5 className="mb-1.5 text-sm font-bold text-slate-800 dark:text-slate-200">{section.heading}</h5>
                                        {section.intro && (
                                          <p className="mb-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{section.intro}</p>
                                        )}
                                        {section.points && section.points.length > 0 && (
                                          <ul className="space-y-1.5">
                                            {section.points.map((pt, pi) => (
                                              <li key={pi} className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                                                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                                                <span>
                                                  <span className="font-semibold text-slate-800 dark:text-slate-200">{pt.bold} </span>
                                                  {pt.text}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-400 dark:text-slate-500 italic">No lesson notes added for this day yet.</p>
                                )}
                              </div>

                              {/* Tasks */}
                              {dayData.tasks && dayData.tasks.length > 0 && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <ListChecks className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Tasks</h4>
                                  </div>
                                  <ul className="space-y-2">
                                    {dayData.tasks.map((task, ti) => (
                                      <li key={ti} className="flex items-center gap-2.5 rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                                        {task.label}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Learning Resources */}
                              {dayData.resourceLinks && dayData.resourceLinks.length > 0 && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <BookOpen className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Learning Resources</h4>
                                  </div>
                                  <div className="space-y-4">
                                    {dayData.resourceLinks.map((link, idx) => {
                                      const getYouTubeId = (url) => {
                                        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                                        const match = url.match(regExp);
                                        return match && match[2].length === 11 ? match[2] : null;
                                      };
                                      const videoId = getYouTubeId(link.url);
                                      if (videoId) {
                                        return (
                                          <div key={idx} className="space-y-2">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{link.title}</span>
                                              <a href={link.url} target="_blank" rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-600 dark:text-cyan-400 hover:text-cyan-500">
                                                <ExternalLink className="h-3 w-3" /> Open in YouTube
                                              </a>
                                            </div>
                                            <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-white/10 shadow-lg"
                                              style={{ paddingBottom: '56.25%' }}>
                                              <iframe
                                                className="absolute left-0 top-0 h-full w-full"
                                                src={`https://www.youtube.com/embed/${videoId}`}
                                                title={link.title}
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                allowFullScreen
                                              />
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
                                          className="group flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-white/5 p-4 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                            <ExternalLink className="h-5 w-5" />
                                          </div>
                                          <span className="flex-1 text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{link.title}</span>
                                          <ExternalLink className="h-4 w-4 text-slate-400 dark:text-slate-600 group-hover:text-cyan-600 transition-colors" />
                                        </a>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Coding Task */}
                              {dayData.codingTask && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Code2 className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Coding Task</h4>
                                  </div>
                                  <p className="rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-slate-950/50 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-mono">
                                    {dayData.codingTask}
                                  </p>
                                </div>
                              )}

                              {/* In-Browser IDE */}
                              <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                <div className="flex items-center gap-2">
                                  <Code2 className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">Try It Here</h4>
                                  <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">Powered by Wandbox</span>
                                </div>
                                <MultiCodeEditor dayNumber={dayNum} />
                              </div>

                              {/* Assignment */}
                              {dayData.assignment && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Assignment</h4>
                                  </div>
                                  <p className="rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-slate-950/50 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                                    {dayData.assignment}
                                  </p>
                                </div>
                              )}

                              {/* Hands-on Practice */}
                              {dayData.handsOn && dayData.handsOn.length > 0 && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Lightbulb className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Hands-on Practice</h4>
                                  </div>
                                  <ul className="space-y-2 rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-slate-950/50 p-4">
                                    {dayData.handsOn.map((item, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-cyan-500" />
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Why This Matters */}
                              {dayData.explanation && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5">
                                  <div className="rounded-xl border border-cyan-200 dark:border-cyan-500/20 bg-gradient-to-br from-cyan-50 dark:from-cyan-950/30 to-slate-50 dark:to-slate-900/50 p-5">
                                    <h4 className="mb-2 text-sm font-bold text-cyan-700 dark:text-cyan-400">Why This Matters</h4>
                                    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">{dayData.explanation}</p>
                                  </div>
                                </div>
                              )}

                              {/* Evaluation Checklist */}
                              {dayData.evaluationChecklist && dayData.evaluationChecklist.length > 0 && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Evaluation Checklist</h4>
                                  </div>
                                  <ul className="space-y-2">
                                    {dayData.evaluationChecklist.map((item, idx) => (
                                      <li key={idx} className="flex items-start gap-2.5 rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm text-slate-600 dark:text-slate-400">
                                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Git Task */}
                              {dayData.gitTask && (
                                <div className="border-t border-slate-200 dark:border-white/10 pt-5 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <GitBranch className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Git Task</h4>
                                  </div>
                                  <p className="font-mono rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-slate-950/50 p-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                                    {dayData.gitTask}
                                  </p>
                                </div>
                              )}

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  );
}
